import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Box, Typography, Button, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TextField, Grid, Chip,
  Alert, CircularProgress, MenuItem, Checkbox, Tooltip,
  IconButton, Badge, Autocomplete
} from '@mui/material';
import CategoryIcon from '@mui/icons-material/Category';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import FilterListIcon from '@mui/icons-material/FilterList';
import SelectAllIcon from '@mui/icons-material/SelectAll';
import DeselectIcon from '@mui/icons-material/Deselect';
import SaveIcon from '@mui/icons-material/Save';
import RefreshIcon from '@mui/icons-material/Refresh';

const TIPO_COLORS = {
  ACTIVO: '#1565c0', PASIVO: '#c62828', PATRIMONIO: '#6a1b9a',
  INGRESO: '#2e7d32', GASTO: '#e65100', COSTO: '#e65100',
};

export default function RubroEeffClasificacion() {
  const [empresas, setEmpresas] = useState([]);
  const [empresaId, setEmpresaId] = useState('');
  const [cuentas, setCuentas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [rubrosPatrimonio, setRubrosPatrimonio] = useState([]);
  const [seleccionados, setSeleccionados] = useState(new Set());
  const [rubroMasivo, setRubroMasivo] = useState('');

  useEffect(() => {
    axios.get('/api/contabilidad/empresas')
      .then(r => {
        const todas = Array.isArray(r.data) ? r.data : [];
        setEmpresas(todas);
        const activas = todas.filter(e => e.activo);
        if (activas.length === 1) setEmpresaId(String(activas[0].id));
      })
      .catch(() => {});

    axios.get('/api/contabilidad/rubro-eeff-catalogo')
      .then(r => setRubrosPatrimonio(Array.isArray(r.data?.patrimonio) ? r.data.patrimonio : []))
      .catch(() => {});
  }, []);

  const cargar = useCallback(async () => {
    if (!empresaId) return;
    setLoading(true);
    setError('');
    setSeleccionados(new Set());
    try {
      const r = await axios.get('/api/contabilidad/rubro-eeff-pendientes', { params: { empresa_id: empresaId } });
      setCuentas((Array.isArray(r.data) ? r.data : []).map(c => ({ ...c, rubro_pendiente: '' })));
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  }, [empresaId]);

  const toggleSeleccion = (id) => {
    setSeleccionados(prev => {
      const s = new Set(prev);
      if (s.has(id)) s.delete(id); else s.add(id);
      return s;
    });
  };

  const selectAll = () => setSeleccionados(new Set(cuentas.map(c => c.id)));
  const deselectAll = () => setSeleccionados(new Set());

  const aplicarRubroMasivo = () => {
    if (!rubroMasivo || seleccionados.size === 0) return;
    setCuentas(prev => prev.map(c =>
      seleccionados.has(c.id) ? { ...c, rubro_pendiente: rubroMasivo.toUpperCase() } : c
    ));
    setSuccess(`Rubro "${rubroMasivo.toUpperCase()}" asignado a ${seleccionados.size} cuenta(s). Haga clic en "Guardar cambios" para confirmar.`);
    setTimeout(() => setSuccess(''), 5000);
  };

  const cambiarRubroCuenta = (id, rubro) => {
    setCuentas(prev => prev.map(c =>
      c.id === id ? { ...c, rubro_pendiente: (rubro || '').toUpperCase() } : c
    ));
  };

  const guardar = async () => {
    const conCambios = cuentas.filter(c => c.rubro_pendiente);
    if (conCambios.length === 0) { setError('No hay cambios pendientes por guardar'); return; }
    setSaving(true);
    setError('');
    try {
      const asignaciones = conCambios.map(c => ({ cuenta_id: c.id, rubro_eeff_codigo: c.rubro_pendiente }));
      await axios.put('/api/contabilidad/rubro-eeff-masivo', { asignaciones });
      setSuccess(`${conCambios.length} cuenta(s) clasificadas correctamente.`);
      setTimeout(() => setSuccess(''), 4000);
      await cargar();
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setSaving(false);
    }
  };

  const pendientesGuardar = cuentas.filter(c => c.rubro_pendiente).length;

  // Autocomplete freeSolo para no-patrimonio: sugiere los rubros que el contador ya usó
  // en otras cuentas de esta misma sesión, para no reinventar el código cada vez.
  const rubrosYaUsados = [...new Set(cuentas.map(c => c.rubro_pendiente).filter(Boolean))];

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 2, flexWrap: 'wrap' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <CategoryIcon sx={{ fontSize: 32, color: '#6a1b9a' }} />
          <Box>
            <Typography variant="h5" fontWeight="bold">Clasificación de Rubros EEFF</Typography>
            <Typography variant="body2" color="text.secondary">
              Asigne el rubro de Estados Financieros a cada cuenta — necesario para el Libro de Inventarios y Balances (3.1, 3.19, 3.20, 3.24)
            </Typography>
          </Box>
        </Box>
        <TextField select label="Empresa" size="small" value={empresaId}
          onChange={e => { setEmpresaId(e.target.value); setCuentas([]); }}
          sx={{ minWidth: 260, ml: { sm: 'auto' } }}>
          {empresas.map(e => (
            <MenuItem key={e.id} value={String(e.id)}>
              {e.razon_social}{!e.activo && <span style={{ color: '#999', marginLeft: 6 }}>(Inactiva)</span>}
            </MenuItem>
          ))}
        </TextField>
      </Box>

      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Button variant="contained" startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <FilterListIcon />}
          onClick={cargar} disabled={loading || !empresaId}>
          {loading ? 'Buscando...' : 'Buscar cuentas sin rubro'}
        </Button>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} icon={<CheckCircleIcon />}>{success}</Alert>}

      {cuentas.length === 0 && !loading && (
        <Alert severity="info">
          {empresaId
            ? 'No hay cuentas sin rubro EEFF asignado. ¡Excelente!'
            : 'Seleccione empresa y haga clic en "Buscar cuentas sin rubro".'}
        </Alert>
      )}

      {cuentas.length > 0 && (
        <>
          <Paper variant="outlined" sx={{ p: 1.5, mb: 2, bgcolor: '#f3e5f5' }}>
            <Grid container spacing={1.5} alignItems="center">
              <Grid item>
                <Tooltip title="Seleccionar todas"><IconButton size="small" onClick={selectAll}><SelectAllIcon /></IconButton></Tooltip>
                <Tooltip title="Deseleccionar todas"><IconButton size="small" onClick={deselectAll}><DeselectIcon /></IconButton></Tooltip>
              </Grid>
              <Grid item>
                <Chip label={`${seleccionados.size} seleccionadas`} size="small"
                  color={seleccionados.size > 0 ? 'secondary' : 'default'} />
              </Grid>
              <Grid item xs>
                <Autocomplete freeSolo size="small" options={rubrosYaUsados}
                  inputValue={rubroMasivo}
                  onInputChange={(e, v) => setRubroMasivo(v)}
                  disabled={seleccionados.size === 0}
                  renderInput={(params) => (
                    <TextField {...params} label="Código de rubro para seleccionadas" placeholder="Ej: EFECTI, CXCCOM, VENTAS..." />
                  )}
                  sx={{ minWidth: 280 }} />
              </Grid>
              <Grid item>
                <Button variant="contained" color="secondary" size="small"
                  onClick={aplicarRubroMasivo} disabled={!rubroMasivo || seleccionados.size === 0}>
                  Aplicar a seleccionadas
                </Button>
              </Grid>
              <Grid item>
                <Badge badgeContent={pendientesGuardar} color="warning">
                  <Button variant="contained" color="success" size="small"
                    startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <SaveIcon />}
                    onClick={guardar} disabled={saving || pendientesGuardar === 0}>
                    {saving ? 'Guardando...' : 'Guardar cambios'}
                  </Button>
                </Badge>
              </Grid>
              <Grid item>
                <IconButton size="small" onClick={cargar} title="Recargar"><RefreshIcon fontSize="small" /></IconButton>
              </Grid>
            </Grid>
          </Paper>

          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            {cuentas.length} cuenta(s) sin rubro
            {pendientesGuardar > 0 && ` • ${pendientesGuardar} con cambios pendientes de guardar`}
          </Typography>

          <Paper elevation={2} sx={{ overflow: 'hidden' }}>
            <TableContainer sx={{ maxHeight: 580 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox" sx={{ width: 40 }} />
                    <TableCell sx={{ fontWeight: 'bold', fontSize: 11, width: 90 }}>Código</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', fontSize: 11 }}>Descripción</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', fontSize: 11, width: 110 }}>Tipo</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', fontSize: 11, minWidth: 260 }}>Rubro EEFF</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {cuentas.map((c) => {
                    const sel = seleccionados.has(c.id);
                    const pendiente = c.rubro_pendiente;
                    const color = TIPO_COLORS[c.tipo_cuenta] || '#555';
                    const esPatrimonio = c.tipo_cuenta === 'PATRIMONIO';

                    return (
                      <TableRow key={c.id}
                        sx={{
                          bgcolor: pendiente ? `${color}11` : sel ? '#e8eaf6' : 'inherit',
                          '&:hover': { bgcolor: pendiente ? `${color}22` : '#f3e5f5' }
                        }}>
                        <TableCell padding="checkbox">
                          <Checkbox size="small" checked={sel} onChange={() => toggleSeleccion(c.id)} />
                        </TableCell>
                        <TableCell sx={{ fontSize: 11, fontFamily: 'monospace', fontWeight: 'bold', color: '#1565c0' }}>
                          {c.codigo}
                        </TableCell>
                        <TableCell sx={{ fontSize: 12 }}>{c.descripcion}</TableCell>
                        <TableCell>
                          <Chip label={c.tipo_cuenta} size="small"
                            sx={{ bgcolor: `${color}22`, color, fontWeight: 'bold', fontSize: 10, height: 20 }} />
                        </TableCell>
                        <TableCell>
                          {esPatrimonio ? (
                            <TextField select size="small" fullWidth
                              value={pendiente || ''}
                              onChange={e => cambiarRubroCuenta(c.id, e.target.value)}
                              SelectProps={{ displayEmpty: true }}
                              sx={{ '& .MuiInputBase-root': { bgcolor: pendiente ? `${color}18` : 'transparent', fontSize: 11 } }}>
                              <MenuItem value=""><em>Sin rubro — seleccionar...</em></MenuItem>
                              {rubrosPatrimonio.map(r => (
                                <MenuItem key={r.codigo} value={r.codigo} sx={{ fontSize: 11 }}>
                                  <Chip label={r.codigo} size="small" sx={{ mr: 0.5, fontSize: 9, height: 16, bgcolor: `${color}22`, color }} />
                                  {r.label}
                                </MenuItem>
                              ))}
                            </TextField>
                          ) : (
                            <>
                              <Autocomplete freeSolo size="small" options={rubrosYaUsados}
                                inputValue={pendiente || ''}
                                onInputChange={(e, v) => cambiarRubroCuenta(c.id, v)}
                                renderInput={(params) => (
                                  <TextField {...params}
                                    placeholder={c.rubro_sugerido ? `Sugerido: ${c.rubro_sugerido}` : 'Código de rubro...'}
                                    sx={{ '& .MuiInputBase-root': { bgcolor: pendiente ? `${color}18` : 'transparent', fontSize: 11 } }} />
                                )} />
                              {c.rubro_sugerido && !pendiente && (
                                <Tooltip title={`Aplicar rubro sugerido: ${c.rubro_sugerido}`}>
                                  <Button size="small" variant="text" sx={{ fontSize: 9, mt: 0.3, p: '1px 4px', minWidth: 0 }}
                                    onClick={() => cambiarRubroCuenta(c.id, c.rubro_sugerido)}>
                                    Aplicar sugerido: {c.rubro_sugerido}
                                  </Button>
                                </Tooltip>
                              )}
                            </>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </>
      )}
    </Box>
  );
}
